"use client"


import Link from "next/link";
import { Button } from "@/components/ui/button";
import Image from "next/image";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
    ArrowRight,
    CheckCircle,
    Map,
    Plane,
    Globe,
    Clock,
    Users,
    Star,
    BarChart3,
    Play,
    Award,
    TrendingUp,
    Moon,
    Sun,
    X,
    Calendar,
    Zap,
    Target,
    Rocket,
    Info,
    AlertTriangle,
    AlertCircle,
} from "lucide-react";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { useTheme } from "@/components/theme-provider";
import { useAuth } from "@/context/AuthContext";

export const metadata = {
    title: "FlightDeck - Free Flight Tracking & Planning for Virtual Pilots",
    description: "FlightDeck is the ultimate free platform for virtual pilots. Plan flights, track your progress, join a global community, and connect with VATSIM & IVAO. No subscriptions, no ads, just pure flying.",
    keywords: [
        "flight tracking",
        "flight planning",
        "virtual pilots",
        "VATSIM",
        "IVAO",
        "flight simulation",
        "logbook",
        "community",
        "free",
        "FlightDeck"
    ],
    openGraph: {
        title: "FlightDeck - Free Flight Tracking & Planning for Virtual Pilots",
        description: "Plan, track, and improve your virtual flying experience. Join 200+ pilots on FlightDeck - 100% free, no subscriptions.",
        url: "https://flightdeck.app/",
        siteName: "FlightDeck",
        images: [
            {
                url: "https://flightdeck.app/og-image.png",
                width: 1200,
                height: 630,
                alt: "FlightDeck - Free Flight Tracking & Planning for Virtual Pilots"
            }
        ],
        locale: "en_US",
        type: "website"
    },
    twitter: {
        card: "summary_large_image",
        title: "FlightDeck - Free Flight Tracking & Planning for Virtual Pilots",
        description: "Plan, track, and improve your virtual flying experience. Join 200+ pilots on FlightDeck – 100% free, no subscriptions.",
        images: ["https://flightdeck.app/og-image.png"]
    }
};

const navigationLinks = [
    { name: "Features", href: "#features" },
    { name: "Roadmap", href: "#roadmap" },
    { name: "Pricing", href: "#pricing" },
    { name: "Documentation", href: "/pages" },
    { name: "FAQ", href: "#faq" },
    { name: "How it works", href: "#how-it-works" },
];

const features = [
        {
        title: "Contracts & Dispatch",
        description: "Accept flight contracts and receive automated dispatch briefings. Fly assigned routes, earn rewards, and experience realistic airline operations.",
        icon: <Award className="h-6 w-6" />,
        image: "/images/mcdu.webp",
        alt: "Flight dispatch and contract briefing"
    },
    {
        title: "Live Tracking",
        description: "Track your flights and other pilots in real-time with our live tracking system integrated with VATSIM and IVAO.",
        icon: <Plane className="h-6 w-6" />,
        image: "/images/live-tracking.webp",
        alt: "Aircraft tracking on radar display"
    },
    {
        title: "Logbook",
        description: "Keep track of your flights with our comprehensive logbook. Log hours, routes, and aircraft with ease.",
        icon: <Clock className="h-6 w-6" />,
        image: "https://images.unsplash.com/photo-1556075798-4825dfaaf498?w=400&h=300&fit=crop",
        alt: "Digital flight logbook interface"
    },
    {
        title: "Global Community",
        description: "Connect with pilots from around the world. Share experiences, participate in events, and fly together.",
        icon: <Globe className="h-6 w-6" />,
        image: "/images/community.webp",
        alt: "Pilots from around the world"
    },
];

const stats = [
    { value: "200+", label: "Active Pilots", icon: <Users className="h-5 w-5" /> },
    { value: "7.5K+", label: "Flights Tracked", icon: <BarChart3 className="h-5 w-5" /> },
    { value: "200+", label: "Countries", icon: <Globe className="h-5 w-5" /> },
    { value: "4.7", label: "User Rating", icon: <Star className="h-5 w-5" /> },
];

const testimonials = [
    {
        quote: "FlightDeck transformed my virtual flying experience. The community events are incredible!",
        author: "Captain Sarah M.",
        role: "VATSIM Pilot",
        avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=100&h=100&fit=crop&crop=face",
        flights: "1,200+ flights"
    },
    {
        quote: "The best flight tracking platform I've ever used. Simple, powerful, and completely free.",
        author: "Michael R.",
        role: "IVAO Member",
        avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face",
        flights: "850+ flights"
    },
    {
        quote: "Finally, a platform that understands what virtual pilots need. Highly recommended!",
        author: "Emma L.",
        role: "Flight Instructor",
        avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face",
        flights: "2,100+ flights"
    },
];

const roadmapItems = [
    {
        quarter: "Q1 2025",
        status: "completed",
        title: "Platform Redesign & Modern Interface",
        description: "A complete overhaul of the FlightDeck interface for improved usability and performance.",
        features: [
            "New user interface",
            "Faster load times",
            "Enhanced mobile experience",
            "Accessibility improvements"
        ]
    },
    {
        quarter: "Q2 2025",
        status: "completed",
        title: "Backend Infrastructure Overhaul",
        description: "A complete rewrite of the backend services to improve performance and scalability.",
        features: [
            "Microservices architecture",
            "Database optimization",
            "API rate limiting",
            "Real-time data processing"
        ]
    },
    {
        quarter: "Q3 2025",
        status: "completed",
        title: "First Flight - Core Features Launch",
        description: "Launch of essential flight tracking and planning features for the virtual aviation community.",
        features: [
            "VATSIM integration",
            "Basic flight logging",
            "Route planning tools",
            "Social features"
        ]
    },
    {
        quarter: "Q4 2025",
        status: "completed",
        title: "FlightDeck v2.1 - Community Platform",
        description: "Major expansion with social features, virtual airlines, and collaborative tools.",
        features: [
            "Advanced airline flight planner",
            "Shared cockpit support",
            "IVAO integration",
        ]
    }
];

// Dark mode hook
function useDarkMode() {
    const [mounted, setMounted] = useState(false);
    const { setTheme } = useTheme();

    useEffect(() => {
        setMounted(true);
    }, []);

    const toggleDarkMode = () => {
        setTheme(isDark ? 'light' : 'dark');
    };

    // Get current theme from theme provider
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    // Return default values during SSR
    if (!mounted) {
        return { isDark: false, toggleDarkMode: () => { } };
    }

    return { isDark, toggleDarkMode };
}

// Smooth scroll utility function
const smoothScrollTo = (elementId: string) => {
    if (elementId === '#top') {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
        return;
    }

    const element = document.getElementById(elementId);
    if (element) {
        element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
        });
    }
};

interface StatusResponse {
    status: string;
    data: {
        maintenance_mode: boolean;
        banner_enabled: boolean;
        banner_message: string;
        banner_type: 'info' | 'warning' | 'error';
        banner_platform_only: boolean;
    };
}

export default function Home() {
    const { isDark, toggleDarkMode } = useDarkMode();
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [roadmapProgress, setRoadmapProgress] = useState(0);
    const [status, setStatus] = useState<StatusResponse | null>(null);
    const { user } = useAuth();

    // Track roadmap scroll progress
    useEffect(() => {
        const handleScroll = () => {
            const roadmapSection = document.getElementById('roadmap');
            if (!roadmapSection) return;

            const rect = roadmapSection.getBoundingClientRect();
            const sectionHeight = roadmapSection.offsetHeight;
            const windowHeight = window.innerHeight;

            // Use the middle of the viewport as the reference point
            const referenceY = windowHeight / 2;

            // Calculate how far the reference point has moved through the section
            const sectionTopAtRef = rect.top - referenceY;
            const sectionBottomAtRef = rect.bottom - referenceY;

            let progress = 0;
            if (sectionTopAtRef < 0 && sectionBottomAtRef > 0) {
                // Reference point is inside the section
                progress = ((referenceY - rect.top) / sectionHeight) * 100;
            } else if (sectionTopAtRef >= 0) {
                // Section is below the reference point
                progress = 0;
            } else if (sectionBottomAtRef <= 0) {
                // Section is above the reference point
                progress = 100;
            }

            setRoadmapProgress(Math.min(100, Math.max(0, progress)));
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Initial call

        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Close mobile menu on route change
    useEffect(() => {
        const handleRouteChange = () => {
            setMobileMenuOpen(false);
        };

        window.addEventListener('resize', handleRouteChange);
        return () => window.removeEventListener('resize', handleRouteChange);
    }, []);

    useEffect(() => {
        fetch(process.env.NEXT_PUBLIC_API_URL + '/status')
            .then(response => response.json())
            .then(data => {
                const statusResponse: StatusResponse = data;
                setStatus(statusResponse);
            })
            .catch(error => {
                console.error('Error fetching API status:', error);
            });
    }, []);

    const navigateTo = (section: string) => {
        if (section.startsWith('#')) {
            smoothScrollTo(section.replace('#', ''));
        } else if (section.startsWith('/')) {
            window.location.href = section;
        }
        setMobileMenuOpen(false);
    };

    return (
        <div className="flex min-h-screen flex-col relative bg-neutral-100 dark:bg-neutral-900 scroll-smooth transition-colors duration-300">
            {/* Banner - Top of page */}
            {status?.data && status?.data.banner_enabled && status?.data.banner_message && 
             (!status?.data.banner_platform_only || (status?.data.banner_platform_only && user)) && (
                <div
                    className={`w-full py-3 px-4 flex items-center justify-center gap-3 shadow-md z-50 ${
                        status.data.banner_type === 'info'
                            ? 'bg-blue-50 dark:bg-blue-950/50 border-b border-blue-200 dark:border-blue-800'
                            : status.data.banner_type === 'warning'
                            ? 'bg-amber-50 dark:bg-amber-950/50 border-b border-amber-200 dark:border-amber-800'
                            : 'bg-red-50 dark:bg-red-950/50 border-b border-red-200 dark:border-red-800'
                    }`}
                >
                    {status.data.banner_type === 'info' && (
                        <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                    )}
                    {status.data.banner_type === 'warning' && (
                        <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                    )}
                    {status.data.banner_type === 'error' && (
                        <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                    )}
                    <p
                        className={`text-sm font-medium text-center ${
                            status.data.banner_type === 'info'
                                ? 'text-blue-900 dark:text-blue-100'
                                : status.data.banner_type === 'warning'
                                ? 'text-amber-900 dark:text-amber-100'
                                : 'text-red-900 dark:text-red-100'
                        }`}
                    >
                        {status.data.banner_message}
                    </p>
                </div>
            )}

            {/* Natural background with subtle gradient */}
            <div className="fixed inset-0 -z-50">
                <div className="absolute inset-0 bg-gradient-to-br from-stone-50 via-neutral-50 to-neutral-100 dark:from-stone-900 dark:via-neutral-900 dark:to-neutral-900 transition-colors duration-300" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(0,0,0,0.01),transparent_70%)] dark:bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.01),transparent_70%)]" />
            </div>

            {/* Navigation with glass effect */}
            <header className={cn("sticky top-4 mt-4 z-40 w-[95%] max-w-6xl rounded-2xl shadow-xl mx-auto backdrop-blur-3xl bg-white/40 dark:bg-neutral-900/90 border border-stone-200/50 dark:border-neutral-700/50 transition-colors duration-300")}>
                <div className={cn("transition-all duration-300 overflow-hidden",
                    mobileMenuOpen ? "h-100" : "h-16")}
                >
                    <div className="flex items-center h-16 justify-between px-6 transition-all duration-300">
                        <button onClick={() => { smoothScrollTo('#top') }} className="bg-transparent flex items-center gap-3 cursor-pointer">
                            <div className="flex h-10 w-10 items-center justify-center rounded-xl dark:bg-transparent bg-stone-800 text-white shadow-lg transition-colors duration-300">
                                <Image src="/fd-logo.svg" alt="FlightDeck Logo" className="" width={32} height={32} />
                            </div>
                            <span className="text-xl font-bold text-stone-900 dark:text-stone-100 transition-colors duration-300">FlightDeck</span>
                        </button>

                        <nav className="hidden lg:flex items-center gap-8">
                            {navigationLinks.map((link) => (
                                <button
                                    key={link.name}
                                    onClick={() => navigateTo(link.href)}
                                    className="text-sm font-medium text-stone-600 dark:text-stone-300 transition-all duration-200 hover:text-stone-900 dark:hover:text-stone-100 hover:scale-105 cursor-pointer"
                                >
                                    {link.name}
                                </button>
                            ))}
                        </nav>

                        {/* Dark mode toggle */}
                        <div className="hidden lg:flex justify-between gap-4">
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={toggleDarkMode}
                                className="text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-100 hover:bg-stone-100/50 dark:hover:bg-neutral-800/50 transition-all duration-200"
                            >
                                {isDark ? (
                                    <Sun className="h-4 w-4" />
                                ) : (
                                    <Moon className="h-4 w-4" />
                                )}
                                <span className="sr-only">Toggle dark mode</span>
                            </Button>
                            {user ? (
                                <Link href={`/${user.username}`}>
                                    <Button variant="ghost" className="text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-100 hover:bg-stone-100/50 dark:hover:bg-neutral-800/50 transition-all duration-200" size="sm">
                                        Welcome, {user.name.split(' ')[0]}
                                    </Button>
                                </Link>
                            ) : (
                                <>
                                    <Link href="/login">
                                        <Button variant="ghost" className="text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-100 hover:bg-stone-100/50 dark:hover:bg-neutral-800/50 transition-all duration-200" size="sm">
                                            Sign In
                                        </Button>
                                    </Link>
                                    <Link href="/login">
                                        <Button size="sm" className="bg-stone-800 dark:bg-stone-200 text-white dark:text-stone-800 hover:bg-stone-900 dark:hover:bg-stone-300 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105">
                                            Get Started
                                        </Button>
                                    </Link>
                                </>
                            )}
                        </div>
                        <div
                            className="block lg:hidden relative w-8 h-8 cursor-pointer"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        >
                            <span
                                className={cn(
                                    "absolute top-1/2 left-0 w-full h-0.5 bg-stone-800 dark:bg-stone-200 rounded transition-transform duration-300 origin-center",
                                    mobileMenuOpen ? "rotate-45" : "-translate-y-1"
                                )}
                            />
                            <span
                                className={cn(
                                    "absolute top-1/2 left-0 w-full h-0.5 bg-stone-800 dark:bg-stone-200 rounded transition-transform duration-300 origin-center",
                                    mobileMenuOpen ? "-rotate-45" : "translate-y-1"
                                )}
                            />
                        </div>
                    </div>
                    <div className="flex lg:hidden">
                        <nav className="flex flex-col gap-4 p-4 ml-4 w-full">
                            {navigationLinks.map((link) => (
                                <button
                                    key={link.name}
                                    onClick={() => {
                                        navigateTo(link.href);
                                        setMobileMenuOpen(false);
                                    }}
                                    className="text-left font-medium text-stone-600 dark:text-stone-300 transition-all duration-200 hover:text-stone-900 dark:hover:text-stone-100 hover:scale-105 cursor-pointer"
                                >
                                    {link.name}
                                </button>
                            ))}

                            <div className="flex justify-between gap-8 mt-6">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={toggleDarkMode}
                                    className="text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-100 hover:bg-stone-100/50 dark:hover:bg-neutral-800/50 transition-all duration-200"
                                >
                                    {isDark ? (
                                        <Sun className="h-4 w-4" />
                                    ) : (
                                        <Moon className="h-4 w-4" />
                                    )}
                                    <span className="sr-only">Toggle dark mode</span>
                                </Button>
                                <div className="flex items-center gap-4">
                                    {
                                        user ? (
                                            <Link href={`/${user.username}`}>
                                                <Button variant="ghost" className="text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-100 hover:bg-stone-100/50 dark:hover:bg-neutral-800/50 transition-all duration-200" size="sm">
                                                    Welcome, {user.name.split(' ')[0]}
                                                </Button>
                                            </Link>
                                        ) : (
                                            <>
                                                <Link href="/login">
                                                    <Button variant="ghost" className="text-stone-600 dark:text-stone-300 hover:text-stone-900 dark:hover:text-stone-100 hover:bg-stone-100/50 dark:hover:bg-neutral-800/50 transition-all duration-200" size="sm">
                                                        Sign In
                                                    </Button>
                                                </Link>
                                                <Link href="/login">
                                                    <Button size="sm" className="bg-stone-800 dark:bg-stone-200 text-white dark:text-stone-800 hover:bg-stone-900 dark:hover:bg-stone-300 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105">
                                                        Get Started
                                                    </Button>
                                                </Link>
                                            </>
                                        )}
                                </div>
                            </div>
                        </nav>
                    </div>
                </div>
            </header>

            <main className="flex-1 overflow-x-hidden w-full">
                {/* Hero Section */}
                <section id="hero" className="py-12 md:pb-24 md:pt-20 container mx-auto px-4 relative">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div className="space-y-8">
                            <Badge className="rounded-full px-4 py-2 backdrop-blur-md bg-white/70 dark:bg-neutral-800/70 border border-stone-200/50 dark:border-slate-700/50 shadow-sm w-fit transition-colors duration-300" variant="secondary">
                                <span className="text-xs font-medium text-stone-700 dark:text-stone-300">✈️ FlightDeck is 100% Free</span>
                            </Badge>

                            <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-stone-900 dark:text-stone-100 leading-tight drop-shadow-md transition-colors duration-300">
                                Your Ultimate <span className="text-stone-700 dark:text-stone-300">Flight Simulation</span> Companion
                            </h1>

                            <p className="text-xl text-stone-600 dark:text-stone-400 leading-relaxed transition-colors duration-300">
                                Join <span className="font-bold text-stone-800 dark:text-stone-200">200+ pilots</span> who track, plan, and improve their virtual flying experience with powerful tools designed for virtual pilots.
                                <span className="font-semibold text-stone-900 dark:text-stone-100 block mt-2">No subscriptions, no hidden fees—completely free!</span>
                            </p>

                            <div className="flex flex-col items-center sm:flex-row gap-4 pt-4">
                                <Link href="/login">
                                    <Button size="lg" className="gap-3 bg-stone-800 dark:bg-stone-200 text-white dark:text-stone-800 hover:bg-stone-900 dark:hover:bg-stone-300 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 px-8 py-4">
                                        <Play className="h-5 w-5" />
                                        Start Flying Today
                                        <ArrowRight className="h-4 w-4" />
                                    </Button>
                                </Link>
                                <Button variant="outline" size="lg" className="gap-2 backdrop-blur-sm w-full bg-white/50 dark:bg-neutral-800/50 border border-stone-200/50 dark:border-slate-700/50 hover:bg-white/70 dark:hover:bg-neutral-800/70 hover:scale-105 hover:shadow-lg transition-all duration-300 px-8 py-4">
                                    <Link href="https://www.youtube.com/watch?v=da2JpoICuI8" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                                        <Play className="h-4 w-4" />
                                        Watch Demo
                                    </Link>
                                </Button>
                            </div>

                            <div className="pt-6">
                                <div className="flex flex-wrap items-center gap-6 text-sm text-stone-500 dark:text-stone-400 transition-colors duration-300">
                                    <div className="flex items-center gap-2">
                                        <CheckCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-500" />
                                        <span>Free</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <CheckCircle className="h-4 w-4 text-slate-600 dark:text-slate-500" />
                                        <span>VATSIM & IVAO Support</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <CheckCircle className="h-4 w-4 text-stone-600 dark:text-stone-500" />
                                        <span>No ads, no upsells</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="relative">
                            <div className="relative group">
                                <div className="absolute -inset-4 bg-gradient-to-r from-stone-600/10 to-slate-600/10 rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-500"></div>
                                <div className="relative overflow-hidden rounded-2xl shadow-2xl backdrop-blur-sm bg-white/10 border-2 border-white/20 dark:border-slate-700/30 group-hover:scale-[1.02] transition-all duration-500">
                                    <Image
                                        src="/images/hero.webp"
                                        alt="FlightDeck Dashboard Preview"
                                        width={600}
                                        height={400}
                                        className="w-full h-auto group-hover:scale-110 transition-transform duration-700"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Features Section */}
                <section id="features" className="py-20 relative scroll-mt-20">
                    <div className="container mx-auto px-4">
                        <div className="text-center mb-16">
                            <Badge className="rounded-full px-4 py-2 backdrop-blur-md bg-white/70 dark:bg-neutral-800/70 border border-stone-200/50 dark:border-slate-700/50 shadow-sm mb-4 transition-colors duration-300" variant="secondary">
                                <span className="text-xs font-medium text-stone-700 dark:text-stone-300">⭐ Powerful Features</span>
                            </Badge>
                            <h2 className="text-4xl font-bold mb-6 text-stone-900 dark:text-stone-100 transition-colors duration-300">Everything You Need to <span className="text-stone-700 dark:text-stone-300">Soar Higher</span></h2>
                            <p className="text-xl text-stone-600 dark:text-stone-400 max-w-3xl mx-auto transition-colors duration-300">
                                Discover tools that will transform your virtual flying experience and connect you with a passionate community of pilots.
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {features.map((feature, index) => (
                                <Card key={index} className="md:glass-card group backdrop-blur-md bg-white/70 dark:bg-neutral-800/70 border border-stone-200/50 dark:border-slate-700/50 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 overflow-hidden">
                                    <div className="relative overflow-hidden">
                                        <div className="aspect-video relative group/image">
                                            <Image
                                                src={feature.image}
                                                alt={feature.alt}
                                                width={400}
                                                height={300}
                                                className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110 [transform-style:preserve-3d] hover:[transform:perspective(1000px)_rotateY(15deg)_rotateX(5deg)_scale(1.05)]"
                                            />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent group-hover:from-black/40 transition-all duration-300"></div>
                                        </div>
                                    </div>
                                    <CardHeader>
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="p-2 rounded-lg w-fit bg-stone-100/80 dark:bg-neutral-700/80 text-stone-700 dark:text-stone-300 transition-colors duration-300">
                                                {feature.icon}
                                            </div>
                                            <CardTitle className="text-stone-900 dark:text-stone-100 group-hover:text-stone-700 dark:group-hover:text-stone-300 transition-colors duration-300">{feature.title}</CardTitle>
                                        </div>
                                    </CardHeader>
                                    <CardContent>
                                        <p className="text-stone-600 dark:text-stone-400 leading-relaxed transition-colors duration-300">{feature.description}</p>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Statistics Section */}
                <section id="stats" className="py-20 relative scroll-mt-20">
                    <div className="container mx-auto px-4">
                        <div className="text-center mb-12">
                            <h3 className="text-2xl font-bold text-stone-900 dark:text-stone-100 mb-4 transition-colors duration-300">Join Our Growing Community</h3>
                            <p className="text-stone-600 dark:text-stone-400 transition-colors duration-300">Trusted by pilots worldwide</p>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                            {stats.map((stat, index) => (
                                <div key={index} className="md:glass-card group text-center space-y-4 p-8 rounded-2xl backdrop-blur-md bg-white/60 dark:bg-neutral-800/60 border border-stone-200/50 dark:border-slate-700/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 hover:scale-105">
                                    <div className="mx-auto bg-stone-100/80 dark:bg-neutral-700/80 rounded-2xl w-16 h-16 flex items-center justify-center text-stone-700 dark:text-stone-300 group-hover:scale-110 transition-all duration-300">
                                        {stat.icon}
                                    </div>
                                    <div className="text-4xl font-bold text-stone-900 dark:text-stone-100 transition-colors duration-300">{stat.value}</div>
                                    <div className="text-sm text-stone-600 dark:text-stone-400 font-medium transition-colors duration-300">{stat.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Testimonials Section */}
                <section id="testimonials" className="hidden py-20 relative scroll-mt-20">
                    <div className="container mx-auto px-4">
                        <div className="text-center mb-16">
                            <Badge className="rounded-full px-4 py-2 backdrop-blur-md bg-white/70 dark:bg-neutral-800/70 border border-stone-200/50 dark:border-slate-700/50 shadow-sm mb-4 transition-colors duration-300" variant="secondary">
                                <span className="text-xs font-medium text-stone-700 dark:text-stone-300">💬 What Pilots Say</span>
                            </Badge>
                            <h2 className="text-4xl font-bold mb-6 text-stone-900 dark:text-stone-100 transition-colors duration-300">Loved by Pilots <span className="text-stone-700 dark:text-stone-300">Worldwide</span></h2>
                        </div>

                        <div className="grid md:grid-cols-3 gap-8">
                            {testimonials.map((testimonial, index) => (
                                <Card key={index} className="md:glass-card group backdrop-blur-md bg-white/70 dark:bg-neutral-800/70 border border-stone-200/50 dark:border-slate-700/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                                    <CardContent className="p-6">
                                        <div className="flex items-center gap-4 mb-4">
                                            <div className="relative">
                                                <Image
                                                    src={testimonial.avatar}
                                                    alt={testimonial.author}
                                                    width={60}
                                                    height={60}
                                                    className="rounded-full border-2 border-white shadow-lg group-hover:scale-110 transition-transform duration-300 [transform-style:preserve-3d] hover:[transform:perspective(1000px)_rotateY(15deg)_scale(1.1)]"
                                                />
                                                <div className="absolute -bottom-1 -right-1 bg-emerald-500 rounded-full w-4 h-4 border-2 border-white"></div>
                                            </div>
                                            <div>
                                                <h4 className="font-semibold text-stone-900 dark:text-stone-100 transition-colors duration-300">{testimonial.author}</h4>
                                                <p className="text-sm text-stone-600 dark:text-stone-400 transition-colors duration-300">{testimonial.role}</p>
                                                <p className="text-xs text-stone-700 dark:text-stone-300 font-medium transition-colors duration-300">{testimonial.flights}</p>
                                            </div>
                                        </div>
                                        <blockquote className="text-stone-700 dark:text-stone-300 italic leading-relaxed transition-colors duration-300">
                                            &quot;{testimonial.quote}&quot;
                                        </blockquote>
                                        <div className="flex text-amber-400 mt-4">
                                            {[...Array(5)].map((_, i) => (
                                                <Star key={i} className="h-4 w-4 fill-current" />
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Network Integration Section */}
                <section id="networks" className="py-20 relative scroll-mt-20">
                    <div className="container mx-auto px-4">
                        <div className="flex flex-col lg:flex-row gap-12 items-center">
                            <div className="lg:w-1/2 space-y-8">
                                <Badge className="rounded-full px-4 py-2 backdrop-blur-md bg-white/70 dark:bg-neutral-800/70 border border-stone-200/50 dark:border-slate-700/50 w-fit transition-colors duration-300" variant="outline">
                                    <span className="text-xs font-medium text-stone-700 dark:text-stone-300">🔗 Seamless Integration</span>
                                </Badge>

                                <h2 className="text-4xl font-bold text-stone-900 dark:text-stone-100 transition-colors duration-300">Connect with <span className="text-stone-700 dark:text-stone-300">Major Flight Networks</span></h2>

                                <p className="text-xl text-stone-600 dark:text-stone-400 leading-relaxed transition-colors duration-300">
                                    FlightDeck integrates directly with VATSIM and IVAO networks, allowing you to plan flights, track statistics, and connect with the community all from one platform.
                                </p>

                                <div className="space-y-4">
                                    <div className="group md:glass-card flex items-start gap-4 p-6 rounded-xl backdrop-blur-md bg-white/40 dark:bg-neutral-800/60 border border-white/30 dark:border-slate-700/30 shadow-lg hover:bg-white/80 dark:hover:bg-neutral-800/80 hover:shadow-xl transition-all duration-300 hover:scale-105">
                                        <div className="p-2 rounded-lg bg-emerald-100/80 dark:bg-emerald-900/40 backdrop-blur-sm border border-emerald-200/30 dark:border-emerald-700/30 group-hover:scale-110 transition-transform duration-300">
                                            <TrendingUp className="h-5 w-5 text-emerald-600 dark:text-emerald-500" />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-stone-900 dark:text-stone-100 mb-1 transition-colors duration-300">Real-time Data</h4>
                                            <p className="text-stone-600 dark:text-stone-400 text-sm transition-colors duration-300">Access real time FlightDeck traffic, airline route information, and more.</p>
                                        </div>
                                    </div>
                                    <div className="group md:glass-card flex items-start gap-4 p-6 rounded-xl backdrop-blur-md bg-white/40 dark:bg-neutral-800/60 border border-white/30 dark:border-slate-700/30 shadow-lg hover:bg-white/80 dark:hover:bg-neutral-800/80 hover:shadow-xl transition-all duration-300 hover:scale-105">
                                        <div className="p-2 rounded-lg bg-neutral-100/80 dark:bg-neutral-700/60 backdrop-blur-sm border border-slate-200/30 dark:border-slate-600/30 group-hover:scale-110 transition-transform duration-300">
                                            <Award className="h-5 w-5 text-slate-600 dark:text-slate-400" />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-stone-900 dark:text-stone-100 mb-1 transition-colors duration-300">Single Sign-On</h4>
                                            <p className="text-stone-600 dark:text-stone-400 text-sm transition-colors duration-300">Use your existing VATSIM or IVAO credentials to log in instantly.</p>
                                        </div>
                                    </div>
                                    <div className="group md:glass-card flex items-start gap-4 p-6 rounded-xl backdrop-blur-md bg-white/40 dark:bg-neutral-800/60 border border-white/30 dark:border-slate-700/30 shadow-lg hover:bg-white/80 dark:hover:bg-neutral-800/80 hover:shadow-xl transition-all duration-300 hover:scale-105">
                                        <div className="p-2 rounded-lg bg-stone-100/80 dark:bg-stone-700/60 backdrop-blur-sm border border-stone-200/30 dark:border-stone-600/30 group-hover:scale-110 transition-transform duration-300">
                                            <Users className="h-5 w-5 text-stone-600 dark:text-stone-400" />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-stone-900 dark:text-stone-100 mb-1 transition-colors duration-300">Cross-Network Events</h4>
                                            <p className="text-stone-600 dark:text-stone-400 text-sm transition-colors duration-300">Participate in events across networks with our unified calendar and planning tools.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="lg:w-1/2 relative">
                                <div className="relative group">
                                    <div className="absolute -inset-2 bg-gradient-to-r from-emerald-600/20 to-blue-600/20 rounded-3xl blur-2xl opacity-60 group-hover:blur-3xl transition-all duration-500"></div>
                                    <div className="relative rounded-2xl p-8">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-md mx-auto">
                                            {/* VATSIM Card */}
                                            <div className="md:glass-card group/card backdrop-blur bg-emerald-200/10 dark:bg-neutral-800/20 rounded-xl p-6 border border-white/30 dark:border-slate-700/30 hover:bg-white/30 dark:hover:bg-neutral-800/30 transition-all duration-300 hover:scale-105 hover:shadow-xl">
                                                <div className="text-center space-y-4">
                                                    <div className="relative mx-auto w-16 h-16 bg-emerald-600/20 rounded-xl flex items-center justify-center group-hover/card:scale-110 transition-transform duration-300">
                                                        <Image
                                                            src="/images/vatsim-logo.svg"
                                                            alt="VATSIM Network"
                                                            width={32}
                                                            height={32}
                                                            className="transition-transform duration-300 [transform-style:preserve-3d] group-hover/card:[transform:perspective(1000px)_rotateY(15deg)_scale(1.1)]"
                                                        />
                                                    </div>

                                                    <div>
                                                        <h4 className="text-stone-700 dark:text-stone-300 font-bold text-lg mb-2 transition-colors duration-300">
                                                            VATSIM
                                                        </h4>
                                                        <p className="text-stone-700 dark:text-stone-300 text-sm mb-4 leading-relaxed transition-colors duration-300">
                                                            Virtual Air Traffic<br />Simulation Network
                                                        </p>
                                                    </div>

                                                    <Button
                                                        onClick={() => window.location.href = ('https://flightdeck.me/login')}
                                                        variant="outline"
                                                        className="w-full bg-emerald-600/20 text-stone-900 dark:text-stone-100 border-emerald-500/50 hover:bg-emerald-600/30 hover:border-emerald-400/70 text-sm font-medium transition-all duration-300 hover:scale-105 hover:shadow-lg"
                                                    >
                                                        Connect Now
                                                    </Button>
                                                </div>
                                            </div>

                                            {/* IVAO Card */}
                                            <div className="md:glass-card group/card backdrop-blur-md bg-blue-200/10 dark:bg-neutral-800/20 rounded-xl p-6 border border-white/30 dark:border-slate-700/30 hover:bg-white/30 dark:hover:bg-neutral-800/30 transition-all duration-300 hover:scale-105 hover:shadow-xl">
                                                <div className="text-center space-y-4">
                                                    <div className="relative mx-auto w-16 h-16 bg-blue-600/20 rounded-xl flex items-center justify-center group-hover/card:scale-110 transition-transform duration-300">
                                                        <Image
                                                            src="/images/ivao-logo.svg"
                                                            alt="IVAO Network"
                                                            width={32}
                                                            height={32}
                                                            className="transition-transform duration-300 [transform-style:preserve-3d] group-hover/card:[transform:perspective(1000px)_rotateY(15deg)_scale(1.1)] dark:filter dark:invert(100) dark:brightness-200"
                                                        />
                                                    </div>
                                                    <div>
                                                        <h4 className="text-stone-900 dark:text-stone-100 font-bold text-lg mb-2 transition-colors duration-300">IVAO</h4>
                                                        <p className="text-stone-700 dark:text-stone-300 text-sm mb-4 leading-relaxed transition-colors duration-300">
                                                            International Virtual<br />Aviation Organisation
                                                        </p>
                                                    </div>
                                                    <Button
                                                        variant="outline"
                                                        onClick={() => window.location.href = ('https://flightdeck.me/login')}
                                                        className="w-full bg-blue-600/20 text-stone-900 dark:text-stone-100 border-blue-500/50 hover:bg-blue-600/30 hover:border-blue-400/70 text-sm font-medium transition-all duration-300 hover:scale-105 hover:shadow-lg"
                                                    >
                                                        Connect Now
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Additional Info */}
                                        <div className="mt-8 text-center">
                                            <div className="rounded-full md:glass-card inline-flex items-center gap-2 px-4 py-2 backdrop-blur-sm bg-white/10 border border-white/20 text-stone-900 dark:text-stone-100 text-sm transition-colors duration-300">
                                                <CheckCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-500" />
                                                <span>Instant connection with your existing credentials</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Roadmap Section */}
                <section id="roadmap" className="py-20 relative scroll-mt-20">
                    <div className="container mx-auto px-4">
                        <div className="text-center mb-16">
                            <Badge className="rounded-full px-4 py-2 backdrop-blur-md bg-white/70 dark:bg-neutral-800/70 border border-stone-200/50 dark:border-slate-700/50 shadow-sm mb-4 transition-colors duration-300" variant="secondary">
                                <span className="text-xs font-medium text-stone-700 dark:text-stone-300">🚀 What&apos;s Coming Next</span>
                            </Badge>
                            <h2 className="text-4xl font-bold mb-6 text-stone-900 dark:text-stone-100 transition-colors duration-300">
                                Our <span className="text-stone-700 dark:text-stone-300">Development Roadmap</span>
                            </h2>
                            <p className="text-xl text-stone-600 dark:text-stone-400 max-w-3xl mx-auto transition-colors duration-300">
                                See what exciting features and improvements we&apos;re working on to make FlightDeck even better for the virtual aviation community.
                            </p>
                        </div>

                        <div className="relative">
                            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-0.5 overflow-hidden">
                                <div className="absolute inset-0 bg-gradient-to-b from-stone-300 via-stone-400 to-stone-300 dark:from-stone-600 dark:via-stone-500 dark:to-stone-600"></div>
                                {/* Progress line */}
                                <div
                                    className="absolute left-0 w-full bg-gradient-to-b from-emerald-400 via-emerald-500 to-emerald-600 transition-all duration-300 ease-out"
                                    style={{
                                        height: `${roadmapProgress}%`,
                                        transform: 'translateZ(0)', // Hardware acceleration
                                        filter: 'drop-shadow(0 0 4px rgba(34, 197, 94, 0.5))'
                                    }}
                                ></div>
                            </div>

                            <div className="space-y-12 md:space-y-16">
                                {roadmapItems.map((item, index) => {
                                    const isEven = index % 2 === 0;
                                    const StatusIcon = item.status === 'in-progress' ? Zap : item.status === 'completed' ? CheckCircle : Target;

                                    // Calculate if this item should be "activated" based on scroll progress
                                    // Each item is activated when the progress line reaches it
                                    const itemProgress = ((index + 1) / roadmapItems.length) * 100;
                                    const isActivated = roadmapProgress >= itemProgress - 5; // Activate slightly before the line reaches

                                    return (
                                        <div key={index} className={`relative flex flex-col md:flex-row items-center gap-8 ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                                            {/* Timeline dot with progress animation */}
                                            <div className={`hidden md:block absolute left-1/2 transform -translate-x-1/2 w-4 h-4 rounded-full border-4 border-white dark:border-neutral-900 shadow-lg z-10 transition-all duration-500 ${isActivated
                                                ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 scale-125 shadow-emerald-500/50'
                                                : 'bg-gradient-to-r from-stone-500 to-stone-600 dark:from-stone-400 dark:to-stone-500'
                                                }`}>
                                                {/* Pulse animation for activated dots */}
                                                {isActivated && (
                                                    <div className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-75"></div>
                                                )}
                                            </div>

                                            {/* Content */}
                                            <div className={`w-full md:w-5/12 ${isEven ? 'md:text-right md:pr-8' : 'md:text-left md:pl-8'}`}>
                                                <Card className={`backdrop-blur-md bg-white/70 dark:bg-neutral-800/70 border border-stone-200/50 dark:border-slate-700/50 shadow-lg hover:shadow-xl transition-all duration-500 hover:-translate-y-1 group ${isActivated ? 'ring-2 ring-emerald-500/20 border-emerald-200/50 dark:border-emerald-700/50' : ''
                                                    }`}>
                                                    <CardHeader>
                                                        <div className={`flex items-center gap-3 mb-3 ${isEven ? 'md:justify-end' : 'md:justify-start'} justify-start`}>
                                                            <div className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-500 ${item.status === 'completed'
                                                                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                                                : item.status === 'in-progress'
                                                                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                                                                    : isActivated
                                                                        ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400'
                                                                        : 'bg-stone-100 dark:bg-stone-800/50 text-stone-700 dark:text-stone-400'
                                                                }`}>
                                                                {item.quarter}
                                                            </div>
                                                            <div className={`p-2 rounded-lg transition-all duration-500 ${item.status === 'completed'
                                                                ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                                                                : item.status === 'in-progress'
                                                                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                                                                    : isActivated
                                                                        ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'
                                                                        : 'bg-stone-100 dark:bg-stone-800/50 text-stone-600 dark:text-stone-400'
                                                                }`}>
                                                                <StatusIcon className={`h-4 w-4 ${isActivated && item.status === 'planned' ? 'animate-pulse' : ''}`} />
                                                            </div>
                                                        </div>
                                                        <CardTitle className="text-xl font-bold text-stone-900 dark:text-stone-100 group-hover:text-stone-700 dark:group-hover:text-stone-300 transition-colors duration-300">
                                                            {item.title}
                                                        </CardTitle>
                                                    </CardHeader>
                                                    <CardContent>
                                                        <p className="text-stone-600 dark:text-stone-400 mb-4 leading-relaxed">
                                                            {item.description}
                                                        </p>
                                                        <div className="space-y-2">
                                                            <h4 className="font-semibold text-stone-800 dark:text-stone-200 text-left text-sm">Key Features:</h4>
                                                            <ul className={`space-y-1 text-sm text-stone-600 dark:text-stone-400 ${isEven ? 'md:text-right' : 'md:text-left'} text-left`}>
                                                                {item.features.map((feature, featureIndex) => (
                                                                    <li key={featureIndex} className={`flex items-center gap-2 ${isEven ? 'md:flex-row-reverse md:justify-end' : 'md:flex-row md:justify-start'} flex-row justify-start transition-all duration-300`}>
                                                                        <CheckCircle className={`h-3 w-3 flex-shrink-0 transition-colors duration-500 ${isActivated ? 'text-emerald-500' : 'text-emerald-500/70'
                                                                            }`} />
                                                                        <span>{feature}</span>
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>

                                                        {item.status === 'in-progress' && (
                                                            <div className="mt-4 pt-4 border-t border-stone-200/50 dark:border-stone-700/50">
                                                                <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
                                                                    <Zap className="h-4 w-4 animate-pulse" />
                                                                    <span className="font-medium">Currently in development</span>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Progress indicator for activated planned items */}
                                                        {item.status === 'planned' && isActivated && (
                                                            <div className="mt-4 pt-4 border-t border-emerald-200/50 dark:border-emerald-700/50">
                                                                <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
                                                                    <Target className="h-4 w-4 animate-pulse" />
                                                                    <span className="font-medium">In planning phase</span>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </CardContent>
                                                </Card>
                                            </div>

                                            {/* Spacer for the other side */}
                                            <div className="hidden md:block w-5/12"></div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Call to action - same as before */}
                        <div className="mt-16 text-center">
                            <div className="backdrop-blur-md bg-white/60 dark:bg-neutral-800/60 border border-stone-200/50 dark:border-slate-700/50 rounded-2xl p-8 shadow-lg">
                                <h3 className="text-2xl font-bold text-stone-900 dark:text-stone-100 mb-4">
                                    Have Feature Ideas?
                                </h3>
                                <p className="text-stone-600 dark:text-stone-400 mb-6 max-w-2xl mx-auto">
                                    We love hearing from our community! Share your ideas and help shape the future of FlightDeck.
                                </p>
                                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                    <Button variant="outline" className="gap-2 backdrop-blur-sm bg-white/50 dark:bg-neutral-800/50 border border-stone-200/50 dark:border-slate-700/50 hover:bg-white/70 dark:hover:bg-neutral-800/70 transition-all duration-300">
                                        <Link href="https://flightdeck.me/discord" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                                            <Calendar className="h-4 w-4" />
                                            Join Community Discussions
                                        </Link>
                                    </Button>
                                    <Button className="gap-2 bg-stone-800 dark:bg-stone-200 text-white dark:text-stone-800 hover:bg-stone-900 dark:hover:bg-stone-300 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                                        <Link href="https://discord.com/channels/1403296220293758986/1403301151679451297" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                                            <Rocket className="h-4 w-4" />
                                            Submit Feature Request
                                        </Link>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>


                {/* CTA Section */}
                <section id="cta" className="py-20 relative scroll-mt-20">
                    <div className="container mx-auto px-4">
                        <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                            <Image
                                src="/images/hero.webp"
                                alt="Aircraft cockpit"
                                width={960}
                                height={300}
                                className="w-full h-[200px] sm:h-[300px] md:h-[400px] lg:h-[500px] object-cover rounded-lg shadow"
                            />

                            <div className="absolute inset-0 flex items-center justify-center p-3 sm:p-4 md:p-8">
                                <div className="w-full max-w-xs sm:max-w-lg md:max-w-2xl lg:max-w-4xl backdrop-blur-lg rounded-xl shadow shadow-white/20 dark:shadow-slate-800">
                                    <div className="p-4 sm:p-6 md:p-8 lg:p-12 rounded-xl bg-white/20">
                                        <h2 className="text-xl sm:text-2xl md:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 md:mb-6 text-center leading-tight bg-gradient-to-r from-slate-300 via-white to-slate-300 bg-clip-text text-transparent drop-shadow-lg">
                                            Ready for <span className="bg-gradient-to-r from-amber-300 via-yellow-100 to-amber-300 bg-clip-text text-transparent">Take Off</span>?
                                        </h2>
                                        <p className="text-white mb-4 sm:mb-6 md:mb-8 text-sm sm:text-base md:text-xl max-w-3xl mx-auto leading-relaxed text-center">
                                            Join hundreds of pilots who have transformed their flight simulation experience with FlightDeck&apos;s powerful tools.
                                            <span className="hidden sm:block mt-2 font-semibold bg-gradient-to-r from-slate-200 via-white to-slate-200 bg-clip-text text-transparent drop-shadow-lg">Start your journey today — completely free!</span>
                                        </p>
                                        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                                            <Link href="/login" className="w-full sm:w-auto">
                                                <Button size="lg" className="w-full gap-2 sm:gap-3 backdrop-blur-sm bg-white text-stone-900 hover:bg-white/90 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 px-4 sm:px-6 md:px-10 py-3 md:py-5 text-sm sm:text-base md:text-lg font-semibold">
                                                    <Play className="h-4 w-4" />
                                                    Start Flying Now
                                                    <ArrowRight className="h-4 w-4" />
                                                </Button>
                                            </Link>
                                        </div>
                                        <p className="text-white/70 text-xs sm:text-sm mt-3 sm:mt-4 md:mt-6 text-center">Setup in under 60 seconds</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Pricing Section */}
                <section id="pricing" className="py-20 relative scroll-mt-20">
                    <div className="container mx-auto px-4">
                        <div className="text-center mb-16">
                            <Badge className="rounded-full px-4 py-2 backdrop-blur-md bg-white/70 dark:bg-neutral-800/70 border border-stone-200/50 dark:border-slate-700/50 shadow-sm mb-4 transition-colors duration-300" variant="secondary">
                                <span className="text-xs font-medium text-stone-700 dark:text-stone-300">💰 Free</span>
                            </Badge>
                            <h2 className="text-4xl font-bold mb-6 text-stone-900 dark:text-stone-100 transition-colors duration-300">
                                Why Pay When <span className="text-stone-700 dark:text-stone-300">FlightDeck is Free?</span>
                            </h2>
                            <p className="text-xl text-stone-600 dark:text-stone-400 max-w-3xl mx-auto transition-colors duration-300">
                                Compare FlightDeck&apos;s completely free platform with expensive alternatives. Get premium features without the premium price tag.
                            </p>
                        </div>

                        <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                            <Card className="relative md:glass-card group backdrop-blur-md bg-gradient-to-br from-emerald-50/80 via-white/70 to-emerald-50/80 dark:from-emerald-950/30 dark:via-neutral-800/70 dark:to-emerald-950/30 border-2 border-emerald-200/50 dark:border-emerald-700/50 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden">
                                {/* Popular badge */}
                                <div className="absolute top-4 right-4 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                                    MOST POPULAR
                                </div>

                                <CardHeader className="text-center pb-4">
                                    <div className="mx-auto w-16 h-16 bg-emerald-100 dark:bg-emerald-900/50 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                                        <CheckCircle className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
                                    </div>
                                    <CardTitle className="text-2xl font-bold text-stone-900 dark:text-stone-100 transition-colors duration-300">
                                        FlightDeck
                                    </CardTitle>
                                    <p className="text-stone-600 dark:text-stone-400 transition-colors duration-300">
                                        Professional flight tracking platform
                                    </p>
                                    <div className="mt-4">
                                        <span className="text-5xl font-bold text-emerald-600 dark:text-emerald-400">FREE</span>
                                        <p className="text-sm text-stone-600 dark:text-stone-400 mt-1">No hidden fees</p>
                                    </div>
                                </CardHeader>

                                <CardContent className="space-y-4">
                                    <div className="space-y-3">
                                        {[
                                            "Unlimited flight tracking",
                                            "Advanced route planning",
                                            "Real-time weather data",
                                            "VATSIM & IVAO integration",
                                            "Community events & groups",
                                            "Performance analytics",
                                            "Mobile & desktop access",
                                            "Priority support included"
                                        ].map((feature, index) => (
                                            <div key={index} className="flex items-center gap-3">
                                                <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-500 flex-shrink-0" />
                                                <span className="text-stone-700 dark:text-stone-300">{feature}</span>
                                            </div>
                                        ))}
                                    </div>

                                    <Link href="/login" className="block pt-4">
                                        <Button className="w-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 py-3">
                                            Start Free Today
                                            <ArrowRight className="h-4 w-4 ml-2" />
                                        </Button>
                                    </Link>
                                </CardContent>
                            </Card>

                            {/* Volanta Comparison */}
                            <Card className="md:glass-card group backdrop-blur-md bg-white/60 dark:bg-neutral-800/60 border border-stone-200/50 dark:border-slate-700/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                                <CardHeader className="text-center pb-4">
                                    <div className="mx-auto w-16 h-16 bg-blue-100 dark:bg-blue-900/50 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                                        <Plane className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <CardTitle className="text-2xl font-bold text-stone-900 dark:text-stone-100 transition-colors duration-300">
                                        Volanta
                                    </CardTitle>
                                    <p className="text-stone-600 dark:text-stone-400 transition-colors duration-300">
                                        Premium flight tracking service
                                    </p>
                                    <div className="mt-4">
                                        <span className="text-5xl font-bold text-blue-600 dark:text-blue-400">$8</span>
                                        <span className="text-xl text-stone-600 dark:text-stone-400">/month</span>
                                        <p className="text-sm text-stone-600 dark:text-stone-400 mt-1">$96/year • Limited features</p>
                                    </div>
                                </CardHeader>

                                <CardContent className="space-y-4">
                                    <div className="space-y-3">
                                        {[
                                            { feature: "Flight tracking", included: true },
                                            { feature: "Route planning", included: true },
                                            { feature: "Weather data", included: true },
                                            { feature: "Limited integrations", included: true },
                                            { feature: "Basic analytics", included: true },
                                            { feature: "Mobile access", included: false },
                                            { feature: "Advanced features", included: false },
                                            { feature: "Priority support", included: false }
                                        ].map((item, index) => (
                                            <div key={index} className="flex items-center gap-3">
                                                {item.included ? (
                                                    <CheckCircle className="h-5 w-5 text-blue-600 dark:text-blue-500 flex-shrink-0" />
                                                ) : (
                                                    <X className="h-5 w-5 text-stone-400 dark:text-stone-500 flex-shrink-0" />
                                                )}
                                                <span className={`${item.included ? 'text-stone-700 dark:text-stone-300' : 'text-stone-400 dark:text-stone-500 line-through'}`}>
                                                    {item.feature}
                                                </span>
                                            </div>
                                        ))}
                                    </div>

                                    <Button variant="outline" className="w-full py-3 border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-all duration-300" disabled>
                                        Requires Subscription
                                    </Button>
                                </CardContent>
                            </Card>

                            {/* SimToolkitPro Comparison */}
                            <Card className="md:glass-card group backdrop-blur-md bg-white/60 dark:bg-neutral-800/60 border border-stone-200/50 dark:border-slate-700/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                                <CardHeader className="text-center pb-4">
                                    <div className="mx-auto w-16 h-16 bg-amber-100 dark:bg-amber-900/50 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                                        <Map className="h-8 w-8 text-amber-600 dark:text-amber-400" />
                                    </div>
                                    <CardTitle className="text-2xl font-bold text-stone-900 dark:text-stone-100 transition-colors duration-300">
                                        SimToolkitPro
                                    </CardTitle>
                                    <p className="text-stone-600 dark:text-stone-400 transition-colors duration-300">
                                        Flight simulation toolkit
                                    </p>
                                    <div className="mt-4">
                                        <span className="text-5xl font-bold text-amber-600 dark:text-amber-400">Free</span>
                                        <p className="text-sm text-stone-600 dark:text-stone-400 mt-1">Limited features</p>
                                    </div>
                                </CardHeader>

                                <CardContent className="space-y-4">
                                    <div className="space-y-3">
                                        {[
                                            { feature: "Basic flight tracking", included: true },
                                            { feature: "Simple route planning", included: true },
                                            { feature: "Weather data access", included: true },
                                            { feature: "Limited network support", included: true },
                                            { feature: "Basic reporting", included: true },
                                            { feature: "No mobile app", included: false },
                                            { feature: "No community features", included: false },
                                            { feature: "Email support only", included: false }
                                        ].map((item, index) => (
                                            <div key={index} className="flex items-center gap-3">
                                                {item.included ? (
                                                    <CheckCircle className="h-5 w-5 text-amber-600 dark:text-amber-500 flex-shrink-0" />
                                                ) : (
                                                    <X className="h-5 w-5 text-stone-400 dark:text-stone-500 flex-shrink-0" />
                                                )}
                                                <span className={`${item.included ? 'text-stone-700 dark:text-stone-300' : 'text-stone-400 dark:text-stone-500 line-through'}`}>
                                                    {item.feature}
                                                </span>
                                            </div>
                                        ))}
                                    </div>

                                    <Button variant="outline" className="w-full py-3 border-amber-200 dark:border-amber-800 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/30 transition-all duration-300" disabled>
                                        Requires Subscription
                                    </Button>
                                </CardContent>
                            </Card>
                        </div>
                    </div>
                </section>

                {/* FAQ Section */}
                <section id="faq" className="py-20 relative scroll-mt-20">
                    <div className="container mx-auto px-4">
                        <div className="text-center mb-16">
                            <Badge className="rounded-full px-4 py-2 backdrop-blur-md bg-white/70 dark:bg-neutral-800/70 border border-stone-200/50 dark:border-slate-700/50 shadow-sm mb-4 transition-colors duration-300" variant="secondary">
                                <span className="text-xs font-medium text-stone-700 dark:text-stone-300">❓ Frequently Asked Questions</span>
                            </Badge>
                            <h2 className="text-4xl font-bold mb-6 text-stone-900 dark:text-stone-100 transition-colors duration-300">Got Questions? <span className="text-stone-700 dark:text-stone-300">We&apos;ve Got Answers</span></h2>
                            <p className="text-xl text-stone-600 dark:text-stone-400 max-w-3xl mx-auto transition-colors duration-300">
                                Find answers to common questions about FlightDeck and get started with confidence.
                            </p>
                        </div>

                        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                            {/* VATSIM/IVAO integration */}
                            <Card className="md:glass-card group backdrop-blur-md bg-white/70 dark:bg-neutral-800/70 border border-stone-200/50 dark:border-slate-700/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                                <CardHeader>
                                    <div className="flex items-start gap-3">
                                        <div className="p-2 rounded-lg bg-emerald-100/80 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-500 group-hover:scale-110 transition-transform duration-300">
                                            <Globe className="h-5 w-5" />
                                        </div>
                                        <div>
                                            <CardTitle className="text-lg font-semibold text-stone-900 dark:text-stone-100 group-hover:text-stone-700 dark:group-hover:text-stone-300 transition-colors duration-300">
                                                How does FlightDeck integrate with VATSIM?
                                            </CardTitle>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-stone-600 dark:text-stone-400 leading-relaxed transition-colors duration-300">
                                        FlightDeck connects seamlessly with VATSIM using your existing credentials. Access live weather, traffic data, and network information all from one unified platform without switching between applications.
                                    </p>
                                </CardContent>
                            </Card>

                            {/* Technical requirements */}
                            <Card className="md:glass-card group backdrop-blur-md bg-white/70 dark:bg-neutral-800/70 border border-stone-200/50 dark:border-slate-700/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                                <CardHeader>
                                    <div className="flex items-start gap-3">
                                        <div className="p-2 rounded-lg bg-blue-100/80 dark:bg-blue-900/40 text-blue-600 dark:text-blue-500 group-hover:scale-110 transition-transform duration-300">
                                            <Zap className="h-5 w-5" />
                                        </div>
                                        <div>
                                            <CardTitle className="text-lg font-semibold text-stone-900 dark:text-stone-100 group-hover:text-stone-700 dark:group-hover:text-stone-300 transition-colors duration-300">
                                                What are the technical requirements?
                                            </CardTitle>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-stone-600 dark:text-stone-400 leading-relaxed transition-colors duration-300">
                                        Simply a device with internet access, any VATSIM transponder (like vPilot) and a modern web browser, that&apos;s it! FlightDeck is completely web-based, so there&apos;s no software to download, install, or maintain. Works on desktop, tablet, and mobile.
                                    </p>
                                </CardContent>
                            </Card>

                            {/* Getting started */}
                            <Card className="md:glass-card group backdrop-blur-md bg-white/70 dark:bg-neutral-800/70 border border-stone-200/50 dark:border-slate-700/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                                <CardHeader>
                                    <div className="flex items-start gap-3">
                                        <div className="p-2 rounded-lg bg-amber-100/80 dark:bg-amber-900/40 text-amber-600 dark:text-amber-500 group-hover:scale-110 transition-transform duration-300">
                                            <Play className="h-5 w-5" />
                                        </div>
                                        <div>
                                            <CardTitle className="text-lg font-semibold text-stone-900 dark:text-stone-100 group-hover:text-stone-700 dark:group-hover:text-stone-300 transition-colors duration-300">
                                                How do I get started with FlightDeck?
                                            </CardTitle>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-stone-600 dark:text-stone-400 leading-relaxed transition-colors duration-300">
                                        Getting started takes less than 60 seconds! Sign in with just your VATSIM profile, and start tracking your flights immediately. No setup, no downloads, no hassle. Just log in and fly.
                                    </p>
                                </CardContent>
                            </Card>

                            {/* Privacy/data */}
                            <Card className="md:glass-card group backdrop-blur-md bg-white/70 dark:bg-neutral-800/70 border border-stone-200/50 dark:border-slate-700/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                                <CardHeader>
                                    <div className="flex items-start gap-3">
                                        <div className="p-2 rounded-lg bg-stone-100/80 dark:bg-stone-700/80 text-stone-600 dark:text-stone-400 group-hover:scale-110 transition-transform duration-300">
                                            <CheckCircle className="h-5 w-5" />
                                        </div>
                                        <div>
                                            <CardTitle className="text-lg font-semibold text-stone-900 dark:text-stone-100 group-hover:text-stone-700 dark:group-hover:text-stone-300 transition-colors duration-300">
                                                How is my data used and protected?
                                            </CardTitle>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-stone-600 dark:text-stone-400 leading-relaxed transition-colors duration-300">
                                        Your privacy is our priority. We use your data solely to provide and improve FlightDeck&apos;s features. Your information is protected with enterprise-grade security, and we never sell your data to third parties. Ever.
                                    </p>
                                </CardContent>
                            </Card>

                            {/* Cost question */}
                            <Card className="md:glass-card group backdrop-blur-md bg-white/70 dark:bg-neutral-800/70 border border-stone-200/50 dark:border-slate-700/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                                <CardHeader>
                                    <div className="flex items-start gap-3">
                                        <div className="p-2 rounded-lg bg-emerald-100/80 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-500 group-hover:scale-110 transition-transform duration-300">
                                            <Star className="h-5 w-5" />
                                        </div>
                                        <div>
                                            <CardTitle className="text-lg font-semibold text-stone-900 dark:text-stone-100 group-hover:text-stone-700 dark:group-hover:text-stone-300 transition-colors duration-300">
                                                Is FlightDeck really free?
                                            </CardTitle>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-stone-600 dark:text-stone-400 leading-relaxed transition-colors duration-300">
                                        Absolutely! FlightDeck&apos;s core features are completely free with no time limits, user restrictions, or hidden fees. We believe powerful flight simulation tools should be accessible to every pilot in the community.
                                    </p>
                                </CardContent>
                            </Card>

                            {/* Support question */}
                            <Card className="md:glass-card group backdrop-blur-md bg-white/70 dark:bg-neutral-800/70 border border-stone-200/50 dark:border-slate-700/50 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                                <CardHeader>
                                    <div className="flex items-start gap-3">
                                        <div className="p-2 rounded-lg bg-blue-100/80 dark:bg-blue-900/40 text-blue-600 dark:text-blue-500 group-hover:scale-110 transition-transform duration-300">
                                            <Users className="h-5 w-5" />
                                        </div>
                                        <div>
                                            <CardTitle className="text-lg font-semibold text-stone-900 dark:text-stone-100 group-hover:text-stone-700 dark:group-hover:text-stone-300 transition-colors duration-300">
                                                How can I get help or support?
                                            </CardTitle>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-stone-600 dark:text-stone-400 leading-relaxed transition-colors duration-300">
                                        Our community and support team are here to help! Join our Discord server for real-time assistance, browse our documentation, or contact our support team directly through the help center.
                                    </p>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Still have questions CTA */}
                        <div className="mt-16 text-center">
                            <div className="backdrop-blur-md bg-white/60 dark:bg-neutral-800/60 border border-stone-200/50 dark:border-slate-700/50 rounded-2xl p-8 shadow-lg">
                                <h3 className="text-2xl font-bold text-stone-900 dark:text-stone-100 mb-4 transition-colors duration-300">
                                    Still Have Questions?
                                </h3>
                                <p className="text-stone-600 dark:text-stone-400 mb-6 max-w-2xl mx-auto transition-colors duration-300">
                                    Our friendly community and support team are always ready to help you get the most out of FlightDeck.
                                </p>
                                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                    <Button variant="outline" className="gap-2 backdrop-blur-sm bg-white/50 dark:bg-neutral-800/50 border border-stone-200/50 dark:border-slate-700/50 hover:bg-white/70 dark:hover:bg-neutral-800/70 hover:scale-105 transition-all duration-300">
                                        <Link href="https://flightdeck.me/discord" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                                            <Users className="h-4 w-4" />
                                            Join Discord Community
                                        </Link>
                                    </Button>
                                    <Button className="gap-2 bg-stone-800 dark:bg-stone-200 text-white dark:text-stone-800 hover:bg-stone-900 dark:hover:bg-stone-300 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                                        <Link href="https://discord.com/channels/1403296220293758986/1403302143137681470" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                                            <Clock className="h-4 w-4" />
                                            Contact Support
                                        </Link>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* How It Works Section */}
                <section id="how-it-works" className="py-20 relative scroll-mt-20">
                    <div className="container mx-auto px-4">
                        <div className="text-center mb-16">
                            <Badge className="rounded-full px-4 py-2 backdrop-blur-md bg-white/70 dark:bg-neutral-800/70 border border-stone-200/50 dark:border-slate-700/50 shadow-sm mb-4 transition-colors duration-300" variant="secondary">
                                <span className="text-xs font-medium text-stone-700 dark:text-stone-300">🛠️ How It Works</span>
                            </Badge>
                            <h2 className="text-4xl font-bold mb-6 text-stone-900 dark:text-stone-100 transition-colors duration-300">
                                Get Started with <span className="text-stone-700 dark:text-stone-300">FlightDeck in 3 Easy Steps</span>
                            </h2>
                            <p className="text-xl text-stone-600 dark:text-stone-400 max-w-3xl mx-auto transition-colors duration-300">
                                Follow these simple steps to connect with major flight networks and enhance your virtual flying experience.
                            </p>
                        </div>

                        <div className="relative">
                            {/* Connection line between steps */}
                            <div className="hidden lg:block absolute top-1/2 left-1/4 right-1/4 h-0.5 bg-gradient-to-r from-emerald-200 via-emerald-300 to-emerald-200 dark:from-emerald-700 dark:via-emerald-600 dark:to-emerald-700 transform -translate-y-1/2"></div>

                            <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
                                {/* Step 1: Connect your account */}
                                <Card className="md:glass-card group relative backdrop-blur-md bg-white/70 dark:bg-neutral-800/70 border border-stone-200/50 dark:border-slate-700/50 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 overflow-hidden px-2 py-6 md:p-6">
                                    {/* Step number badge */}
                                    <div className="mx-auto w-12 h-12 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg z-10 group-hover:scale-110 transition-transform duration-300">
                                        1
                                    </div>

                                    <CardHeader className="text-center pt-12 pb-6">
                                        <div className="mx-auto w-20 h-20 bg-gradient-to-br from-emerald-100 to-emerald-200 dark:from-emerald-900/40 dark:to-emerald-800/40 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-300 shadow-lg">
                                            <div className="p-3 rounded-xl bg-emerald-600/20 backdrop-blur-sm border border-emerald-500/30 group-hover:rotate-6 transition-transform duration-300">
                                                <Users className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
                                            </div>
                                        </div>
                                        <CardTitle className="text-xl font-bold text-stone-900 dark:text-stone-100 group-hover:text-stone-700 dark:group-hover:text-stone-300 transition-colors duration-300 mb-3">
                                            Connect Your Account
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="text-center space-y-4">
                                        <p className="text-stone-600 dark:text-stone-400 leading-relaxed transition-colors duration-300">
                                            Link your VATSIM account to FlightDeck for seamless integration and enhanced features.
                                        </p>
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-center gap-2 text-sm text-stone-700 dark:text-stone-300">
                                                <CheckCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-500" />
                                                <span>Single sign-on</span>
                                            </div>
                                            <div className="flex items-center justify-center gap-2 text-sm text-stone-700 dark:text-stone-300">
                                                <CheckCircle className="h-4 w-4 text-emerald-600 dark:text-emerald-500" />
                                                <span>Secure connection</span>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Step 2: Plan your flight */}
                                <Card className="md:glass-card group relative backdrop-blur-md bg-white/70 dark:bg-neutral-800/70 border border-stone-200/50 dark:border-slate-700/50 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 overflow-hidden px-2 py-6 md:p-6">
                                    {/* Step number badge */}
                                    <div className="mx-auto w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg z-10 group-hover:scale-110 transition-transform duration-300">
                                        2
                                    </div>

                                    <CardHeader className="text-center pt-12 pb-6">
                                        <div className="mx-auto w-20 h-20 bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/40 dark:to-blue-800/40 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-300 shadow-lg">
                                            <div className="p-3 rounded-xl bg-blue-600/20 backdrop-blur-sm border border-blue-500/30 group-hover:rotate-6 transition-transform duration-300">
                                                <Map className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                                            </div>
                                        </div>
                                        <CardTitle className="text-xl font-bold text-stone-900 dark:text-stone-100 group-hover:text-stone-700 dark:group-hover:text-stone-300 transition-colors duration-300 mb-3">
                                            Plan Your Flight
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="text-center space-y-4">
                                        <p className="text-stone-600 dark:text-stone-400 leading-relaxed transition-colors duration-300">
                                            Use our advanced tools to plan your flight route, check weather conditions, and calculate fuel requirements.
                                        </p>
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-center gap-2 text-sm text-stone-700 dark:text-stone-300">
                                                <CheckCircle className="h-4 w-4 text-blue-600 dark:text-blue-500" />
                                                <span>Weather integration</span>
                                            </div>
                                            <div className="flex items-center justify-center gap-2 text-sm text-stone-700 dark:text-stone-300">
                                                <CheckCircle className="h-4 w-4 text-blue-600 dark:text-blue-500" />
                                                <span>Route optimization</span>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Step 3: Track and analyze */}
                                <Card className="md:glass-card group relative backdrop-blur-md bg-white/70 dark:bg-neutral-800/70 border border-stone-200/50 dark:border-slate-700/50 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 overflow-hidden px-2 py-6 md:p-6">
                                    {/* Step number badge */}
                                    <div className="mx-auto w-12 h-12 bg-gradient-to-r from-amber-500 to-amber-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg z-10 group-hover:scale-110 transition-transform duration-300 ">
                                        3
                                    </div>

                                    <CardHeader className="text-center pt-12 pb-6">
                                        <div className="mx-auto w-20 h-20 bg-gradient-to-br from-amber-100 to-amber-200 dark:from-amber-900/40 dark:to-amber-800/40 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-300 shadow-lg">
                                            <div className="p-3 rounded-xl bg-amber-600/20 backdrop-blur-sm border border-amber-500/30 group-hover:rotate-6 transition-transform duration-300">
                                                <BarChart3 className="h-8 w-8 text-amber-600 dark:text-amber-400" />
                                            </div>
                                        </div>
                                        <CardTitle className="text-xl font-bold text-stone-900 dark:text-stone-100 group-hover:text-stone-700 dark:group-hover:text-stone-300 transition-colors duration-300 mb-3">
                                            Track & Analyze
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="text-center space-y-4">
                                        <p className="text-stone-600 dark:text-stone-400 leading-relaxed transition-colors duration-300">
                                            Fly your virtual aircraft and track your performance with real-time analytics and detailed insights.
                                        </p>
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-center gap-2 text-sm text-stone-700 dark:text-stone-300">
                                                <CheckCircle className="h-4 w-4 text-amber-600 dark:text-amber-500" />
                                                <span>Real-time tracking</span>
                                            </div>
                                            <div className="flex items-center justify-center gap-2 text-sm text-stone-700 dark:text-stone-300">
                                                <CheckCircle className="h-4 w-4 text-amber-600 dark:text-amber-500" />
                                                <span>Performance analytics</span>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        </div>

                        {/* Bottom CTA */}
                        <div className="mt-16 text-center">
                            <div className="backdrop-blur-md bg-white/60 dark:bg-neutral-800/60 border border-stone-200/50 dark:border-slate-700/50 rounded-2xl p-8 shadow-lg max-w-2xl mx-auto">
                                <h3 className="text-2xl font-bold text-stone-900 dark:text-stone-100 mb-4 transition-colors duration-300">
                                    Ready to Start Your Journey?
                                </h3>
                                <p className="text-stone-600 dark:text-stone-400 mb-6 transition-colors duration-300">
                                    Join 200+ pilots who have already transformed their virtual flying experience with FlightDeck.
                                </p>
                                <Link href="/login">
                                    <Button className="gap-3 bg-stone-800 dark:bg-stone-200 text-white dark:text-stone-800 hover:bg-stone-900 dark:hover:bg-stone-300 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 px-8 py-4">
                                        <Rocket className="h-5 w-5" />
                                        Get Started Now
                                        <ArrowRight className="h-4 w-4" />
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            {/* Footer */}
            <footer className="backdrop-blur-md bg-white/80 dark:bg-neutral-900/80 border-t border-stone-200/50 dark:border-slate-700/50 py-12 transition-colors duration-300">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
                        <div className="md:col-span-2">
                            <div className="flex items-center gap-3 mb-6">
                                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-stone-800 dark:bg-transparent text-white dark:text-stone-800 shadow-lg transition-colors duration-300">
                                    <Image src="/fd-logo.svg" alt="FlightDeck Logo" width={20} height={20} />
                                </div>
                                <span className="text-xl font-bold text-stone-900 dark:text-stone-100 transition-colors duration-300">FlightDeck</span>
                            </div>
                            <p className="text-stone-600 dark:text-stone-400 mb-6 max-w-md leading-relaxed transition-colors duration-300">
                                Enhancing your virtual flight experience with powerful tools and community features. Join hundreds of pilots worldwide and soar to new heights.
                            </p>
                            <div className="flex items-center gap-4">
                                <Link href="https://www.youtube.com/@DutchSimPilot" className="flex items-center justify-center bg-red-600 hover:bg-red-700 w-10 h-10 rounded-xl transition-all duration-200 hover:scale-110 shadow-lg" target="_blank" rel="noopener noreferrer">
                                    <svg viewBox="0 -3 20 20" version="1.1" xmlns="http://www.w3.org/2000/svg" className="size-4"><title>youtube [#168]</title><desc>Created with Sketch.</desc><defs></defs><g id="Page-1" stroke="none" strokeWidth="1" fillRule="evenodd" className="fill-primary-foreground dark:fill-white"><g id="Dribbble-Light-Preview" transform="translate(-300.000000, -7442.000000)"><g id="icons" transform="translate(56.000000, 160.000000)"><path d="M251.988432,7291.58588 L251.988432,7285.97425 C253.980638,7286.91168 255.523602,7287.8172 257.348463,7288.79353 C255.843351,7289.62824 253.980638,7290.56468 251.988432,7291.58588 M263.090998,7283.18289 C262.747343,7282.73013 262.161634,7282.37809 261.538073,7282.26141 C259.705243,7281.91336 248.270974,7281.91237 246.439141,7282.26141 C245.939097,7282.35515 245.493839,7282.58153 245.111335,7282.93357 C243.49964,7284.42947 244.004664,7292.45151 244.393145,7293.75096 C244.556505,7294.31342 244.767679,7294.71931 245.033639,7294.98558 C245.376298,7295.33761 245.845463,7295.57995 246.384355,7295.68865 C247.893451,7296.0008 255.668037,7296.17532 261.506198,7295.73552 C262.044094,7295.64178 262.520231,7295.39147 262.895762,7295.02447 C264.385932,7293.53455 264.28433,7285.06174 263.090998,7283.18289" id="youtube-[#168]"></path></g></g></g></svg>
                                </Link>
                            </div>
                        </div>
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold text-stone-900 dark:text-stone-100 transition-colors duration-300">Quick Links</h3>
                            <ul className="space-y-2">
                                <li>
                                    <Link href="/#features" className="text-stone-600 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200 transition-colors duration-300">Features</Link>
                                </li>
                                <li>
                                    <Link href="/#pricing" className="text-stone-600 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200 transition-colors duration-300">Pricing</Link>
                                </li>
                                <li>
                                    <Link href="/#community" className="text-stone-600 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200 transition-colors duration-300">Community</Link>
                                </li>
                                <li>
                                    <Link href="/pages" className="text-stone-600 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200 transition-colors duration-300">Documentation</Link>
                                </li>
                                <li>
                                    <Link href="/privacy" className="text-stone-600 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200 transition-colors duration-300">Privacy Statement</Link>
                                </li>
                                <li>
                                    <Link href="/terms" className="text-stone-600 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200 transition-colors duration-300">Terms of Service</Link>
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div className="text-center text-stone-500 dark:text-stone-400 text-sm">
                        &copy; {new Date().getFullYear()} FlightDeck. All rights reserved.
                    </div>
                </div>
            </footer >
        </div >
    );
}